import React from 'react';
import styled from "styled-components";
import moment from "moment";
import Colours from '@shared/ui/atoms/Colours';
import DateFlicker, { DateScale } from '@shared/ui/molecules/DateFlicker';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import { BodyRegular } from '@shared/ui/atoms/fonts/Body';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectCalendarDate, setCalendarDate } from '@shared/store/dashboard.duck';
import { PRETTY_DATE_NO_ORDINAL_FORMAT, PRETTY_FULL_DAY_DATE_FORMAT } from "@shared/utils/DateUtils";
import { TeamMultiSelectWithFavourites } from '@shared/ui/organisms/team-drop-down-list/TeamMultiSelectWithFavourites';
import { selectAllTeams } from '@shared/store/config.duck';
import { selectAllTags } from '@shared/store/userTags.duck';
import { WithDaysInWorkWeek } from '@shared/hooks/WithDaysInWorkWeek';
import { MovementsDayContainer } from "@shared/ui/organisms/movements-wallchart/components/StructuralComponents";
import { desktopSmall, smallTablet, tablet } from "@shared/ui/atoms/MediaQueries";
import { MainWhereaboutsContent } from "@features/whereabouts/pages/whereabouts-v2/Structure";
import { V2Colours } from "./WhereaboutsV2Styles";
import { PageView, ViewMode } from "./types";
import {
  selectDailyFilter,
  selectGlobalHeaderCollapsed,
  selectNotesPanelOpen,
  selectPageView,
  selectSidebarOpen,
  selectViewMode,
  setViewMode,
  toggleSidebar,
} from "@features/whereabouts/store/whereaboutsView.duck";
import { applyDailyFilter } from "@features/whereabouts/store/editMovements.duck";
import { DailyFilterBar } from "./DailyFilterBar";
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { redirectTo } from '@shared/store/auth.duck';
import { deepLinkTo, isInTeamsStoreApp } from '@shared/utils/TeamsUtils';
import { uiUrl } from '@shared/services/EnvironmentVariables';
import { useScopeTitle } from '@features/whereabouts/hooks/useScopeTitle';
import { useWhereaboutsScope } from './WhereaboutsScopeContext';

interface Props {
  showMobileSidebarButton?: boolean;
}

export default function WhereaboutsHeader({ showMobileSidebarButton }: Props) {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const calendarDate = useSelector(selectCalendarDate);
  const allTeams = useSelector(selectAllTeams);
  const workGroups = useSelector(selectAllTags);
  const viewMode = useSelector(selectViewMode);
  const pageView = useSelector(selectPageView);
  const sidebarOpen = useSelector(selectSidebarOpen);
  const notesPanelOpen = useSelector(selectNotesPanelOpen);
  const dailyFilter = useSelector(selectDailyFilter);
  const headerCollapsed = useSelector(selectGlobalHeaderCollapsed);
  const daysInWeek = WithDaysInWorkWeek();
  const today = moment();
  const { scope, isScoped, backLink } = useWhereaboutsScope();
  const scopeTitle = useScopeTitle(scope);

  const isGridWeekView = pageView === PageView.Grid && viewMode === ViewMode.Week;

  const onBackClicked = () => {
    if (!backLink) return;
    if (isInTeamsStoreApp() && backLink.teamsTab) {
      deepLinkTo(backLink.teamsTab, `${uiUrl}${backLink.url}`);
    } else {
      dispatch(redirectTo(backLink.url));
    }
  };

  const viewModeLabels: Record<ViewMode, { short: string; full: string }> = {
    [ViewMode.Day]: { short: t('whereabouts-v2.day-short', 'D'), full: t('whereabouts-v2.day-view', 'Day View') },
    [ViewMode.Week]: { short: t('whereabouts-v2.week-short', 'W'), full: t('whereabouts-v2.week-view', 'Week View') },
    [ViewMode.Month]: { short: t('whereabouts-v2.month-short', 'M'), full: t('whereabouts-v2.month-view', 'Month View') },
  };

  const onTodayClicked = () => {
    dispatch(setCalendarDate(moment()));
  }

  return (
    <>
      <Header $collapsed={headerCollapsed}>
        <HeaderInner $sidebarOpen={sidebarOpen} $notesPanelOpen={notesPanelOpen}>
          <Left>
            {isScoped ? (
              <ScopedTitle>
                {backLink && <GoBackButton onClick={onBackClicked} text={backLink.text} url={backLink.url} style={{marginBottom: 4}} />}
                <HeadlineLarge>{scopeTitle ?? viewModeLabels[viewMode].full}</HeadlineLarge>
              </ScopedTitle>
            ) : (
              <HeadlineLarge>{viewModeLabels[viewMode].full}</HeadlineLarge>
            )}
          </Left>
          <ViewModeButtons>
            {Object.values(ViewMode).map((mode) => (
              <ViewModeButton key={mode}
                              $active={viewMode === mode}
                              aria-label={viewModeLabels[mode].full}
                              aria-pressed={viewMode === mode}
                              title={viewModeLabels[mode].full}
                              onClick={() => dispatch(setViewMode(mode))}>
                {viewModeLabels[mode].short}
              </ViewModeButton>
            ))}
          </ViewModeButtons>
          <HeaderRight>
            <OutlineButton text="whereabouts-v2.today"
                           click={onTodayClicked}
                           size={ButtonSize.Small}
                           style={{ padding: '0 16px', maxWidth: 'fit-content', marginRight: 16 }} />
            {showMobileSidebarButton && (
              <MobileSidebarButton onClick={() => dispatch(toggleSidebar())}>
                <Icon icon={IconTypes.Menu} />
              </MobileSidebarButton>
            )}
          </HeaderRight>
        </HeaderInner>
      </Header>
      <DateRow>
        <DateRowInner $sidebarOpen={sidebarOpen} $notesPanelOpen={notesPanelOpen}>
          <DateFlickerContainer>
            <DateFlicker dateScale={viewMode === ViewMode.Day ? DateScale.Day : DateScale.Week}
                         date={calendarDate}
                         updateGlobalDate={true}
                         restrictPastWhereabouts={true}
                         dateFormat={viewMode === ViewMode.Day ? PRETTY_FULL_DAY_DATE_FORMAT : PRETTY_DATE_NO_ORDINAL_FORMAT}
                         className="dateFlicker--no-left-padding" />
          </DateFlickerContainer>
          {isGridWeekView && (
            <GridDayHeaders>
              {daysInWeek.map((day, i) => {
                const isPast = day.isBefore(today, 'day');
                const isToday = day.isSame(today, 'day');
                const colour = isToday ? Colours.blue : isPast ? Colours.mildGrey : Colours.darkGrey;
                return (
                  <GridDayHeader key={i} $isToday={isToday}>
                    <BodyRegular weight={600} colour={colour}>{day.format('ddd')}</BodyRegular>
                    <BodyRegular weight={600} colour={colour}>{day.format('DD')}</BodyRegular>
                  </GridDayHeader>
                );
              })}
            </GridDayHeaders>
          )}
          {viewMode === ViewMode.Month && !isScoped && (
            <TeamSelectWrapper>
              <TeamMultiSelectWithFavourites teams={allTeams}
                                             workGroups={workGroups}
                                             outerClassName={'multiSelect__wrapper'}
                                             multiSelectClassName={'multiSelect__dropDown'}
                                             reloadTeamWhereaboutsOnSelect={true} />
            </TeamSelectWrapper>
          )}
          {((viewMode === ViewMode.Day) || (viewMode === ViewMode.Week && pageView === PageView.Calendar)) && (
            <DailyFilterBar filter={dailyFilter} onChange={(filter) => dispatch(applyDailyFilter(filter))} />
          )}
        </DateRowInner>
      </DateRow>
    </>
  );
}

const Header = styled.div<{ $collapsed: boolean }>`
  background: #FFFFFF;
  overflow: hidden;
  max-height: ${props => props.$collapsed ? '0' : '240px'};
  transition: max-height 0.2s ease;
`;

const HeaderInner = styled(MainWhereaboutsContent)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    "left right"
    "viewModes viewModes";
  row-gap: 8px;
  align-items: center;
  padding: 16px 16px;
  @media (${tablet}) {
    grid-template-columns: 1fr auto 1fr;
    grid-template-areas: "left viewModes right";
    row-gap: 0;
    padding: 16px 0 8px 16px;
    transition: max-width 0.25s ease;
  }
`;

const DateRow = styled.div`
  background: #FFFFFF;
`;

const DateRowInner = styled(MainWhereaboutsContent)`
  display: flex;
  align-items: center;
  padding: 0 16px 0 16px;
  flex-direction: column;
  transition: max-width 0.25s ease;
  
  .dateFlicker--no-left-padding .weekFlicker__control {
    padding-left: 0;
  }

  @media (${desktopSmall}) {
    flex-direction: row;
  }
`;

const DateFlickerContainer = styled.div`
  width: 100%;
  @media (${smallTablet}) {
    max-width: 32%;
  }
`

const ViewModeButtons = styled.div`
  grid-area: viewModes;
  display: flex;
  gap: 4px;
  justify-self: center;
`;

const Left = styled.div`
  grid-area: left;
  display: flex;
  align-items: center;
`;

const ScopedTitle = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  h1, h2, h3 {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
  }
`;

const TeamSelectWrapper = styled.div`
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
  > *,
  > * > * {
    width: 100%;
  }
  .multiSelect__dropDown {
    min-width: 350px;
    & > div {
      min-width: 350px;
    }
  }
  @media (${tablet}) {
    width: auto;
    margin-top: 0;
    margin-bottom: 0;
    margin-left: auto;
    min-width: 350px;
    > *,
    > * > * {
      width: auto;
    }
  }
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  padding: 0;
  border: ${props => props.$active ? '2px' : '1px'} solid ${Colours.blue};
  border-radius: 4px;
  background: ${props => props.$active ? V2Colours.paleCyan : 'transparent'};
  color: ${Colours.blue};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    border-color: ${Colours.blue};
  }
`;

const GridDayHeaders = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  max-width: 100%;
  justify-content: space-between;
  @media (${smallTablet}) {
    width: 68%;
    max-width: 68%;
  }
`;

const GridDayHeader = styled(MovementsDayContainer)<{ $isToday?: boolean }>`
  border-bottom: 3px solid ${props => props.$isToday ? Colours.blue : 'transparent'};
`;

const HeaderRight = styled.div`
  grid-area: right;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const MobileSidebarButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  .icon {
    font-size: 1.2em;
    color: ${Colours.darkGrey};
  }
  @media (${tablet}) {
    display: none;
  }
`;
